/**
 * The read cache: serve instantly, refresh quietly.
 *
 * THE ONE RULE THAT SHAPES EVERYTHING HERE:
 *
 *   **A background refresh updates the CACHE, not the SCREEN.**
 *
 * Pushing a second, later result into a live screen breaks four things that were
 * measured in this codebase, not imagined:
 *   - `usePagination.tryNext` awaits the fetch twice and branches on a ref the
 *     fetch mutated, so a cached first leg makes it probe against the wrong rows
 *     and then double-fetch to roll back;
 *   - `useServerTable` assigns the returned array BY REFERENCE, so a component
 *     that sorts in place would corrupt the stored entry for every other reader;
 *   - 511 `withFrappeLoading` call sites would flash a second spinner;
 *   - list pages run a per-row fan-out after each fetch, so it would run twice and
 *     double the very N+1 burst caching is meant to remove.
 *
 * So the caller gets ONE resolution. The newer data is waiting the next time the
 * screen asks — on navigation, a tab switch, pull-to-refresh, or one of the
 * existing `refreshToken` bumps. A screen that genuinely needs live updates opts
 * in through `onRevalidated`; nothing gets it by default.
 */

import { buildCacheKey, permissionsResolved } from "./cacheKeys";
import type { CacheDecision, CacheOverride } from "./cachePolicy";
import { decideCache } from "./cachePolicy";
import type { CacheEntry } from "./cacheStore";
import { clearAll, deleteByTags, deleteByUser, readEntry, writeEntry } from "./cacheStore";
import { currentUserKey } from "@/offline/user";
import { isOffline } from "@/services/offlineSignal";

export type CacheMode =
  /** Follow the policy table. The default for every read. */
  | "auto"
  /** Go to the network and update the cache. For pollers and manual refresh. */
  | "bypass"
  /** Never touch the network. */
  | "only";

export interface CachedReadOptions<T> {
  url: string;
  params?: unknown;
  method?: string;
  mode?: CacheMode;
  fetcher: () => Promise<T>;
  /** An explicit tier from a call site that knows more than the URL does. */
  override?: CacheOverride | null;
  /**
   * Opt-in live update. Called ONLY when a background refresh produced something
   * different from what the caller was handed. Screens that take this must be able
   * to survive a second render — most cannot, which is why it is opt-in.
   */
  onRevalidated?: (fresh: T) => void;
}

export interface CachedReadResult<T> {
  data: T;
  /** True when this came from the device rather than the network. */
  fromCache: boolean;
  /** When the server actually answered. Surfaced for the asOf tier. */
  cachedAt: number | null;
  /** True when it was served past its tier window (offline, or a slow refresh). */
  stale: boolean;
}

/** In-flight reads, so N screens asking at once produce ONE request. */
const inFlight = new Map<string, Promise<unknown>>();

/** Background refreshes already running, so a burst does not stampede. */
const revalidating = new Set<string>();

/**
 * Hand out a copy, frozen.
 *
 * ⚠️ WITHOUT THIS THE CACHE IS A SHARED MUTABLE OBJECT. `useServerTable` and a
 * dozen list pages assign the returned array straight into a `ref`; Vue wraps it
 * in a reactive proxy whose writes land on the underlying array, so one component
 * sorting its rows in place would rewrite what every other reader sees.
 */
function harden<T>(value: T): T {
  try {
    const copy = typeof structuredClone === "function"
      ? structuredClone(value)
      : JSON.parse(JSON.stringify(value));

    return deepFreeze(copy);
  }
  catch {
    // Not cloneable: hand back the original rather than failing the read. It will
    // not have been stored either (writeEntry refuses what it cannot serialise).
    return value;
  }
}

function deepFreeze<T>(value: T): T {
  if (!value || typeof value !== "object")
    return value;

  Object.freeze(value);

  for (const key of Object.getOwnPropertyNames(value)) {
    const child = (value as Record<string, unknown>)[key];

    if (child && typeof child === "object" && !Object.isFrozen(child))
      deepFreeze(child);
  }

  return value;
}

function isFresh(entry: CacheEntry, decision: CacheDecision): boolean {
  if (!decision.ttlMs)
    return false;

  return Date.now() - entry.cachedAt < decision.ttlMs;
}

async function store<T>(key: string, body: T, decision: CacheDecision): Promise<void> {
  // ⚠️ Never store while we do not yet know who the user is. Phone numbers are
  // blanked per-viewer, and a response fetched during the access-load window has
  // them blanked for EVERYONE — storing it makes that permanent.
  if (!permissionsResolved())
    return;

  await writeEntry({
    key,
    body,
    cachedAt: Date.now(),
    tier: decision.tier!,
    tags: decision.tags ?? [],
    user: currentUserKey() || "-",
  });
}

/**
 * Read through the cache.
 *
 * Never throws on cache trouble: any storage failure falls through to the
 * network, which is exactly today's behaviour.
 */
export async function getCachedOrFetch<T>(options: CachedReadOptions<T>): Promise<CachedReadResult<T>> {
  const decision = decideCache(options.url, options.method, options.override);
  const mode = options.mode ?? "auto";

  if (!decision.cacheable || mode === "bypass") {
    const data = await dedupe(options, null);

    return { data, fromCache: false, cachedAt: null, stale: false };
  }

  const key = buildCacheKey({ url: options.url, params: options.params, method: options.method });
  const entry = await readEntry<T>(key).catch(() => null);

  if (entry) {
    const fresh = isFresh(entry, decision);

    // Offline, age is not a reason to refuse: the stored answer is the only answer
    // there is, and the caller is told how old it is.
    if (fresh || isOffline() || mode === "only") {
      if (!fresh && !isOffline())
        void revalidate(key, options, decision);
      else if (!fresh)
        void 0; // offline: nothing to refresh from

      return {
        data: harden(entry.body),
        fromCache: true,
        cachedAt: entry.cachedAt,
        stale: !fresh,
      };
    }

    // Stale and online: hand back the stored copy NOW and refresh behind it.
    void revalidate(key, options, decision);

    return { data: harden(entry.body), fromCache: true, cachedAt: entry.cachedAt, stale: true };
  }

  if (mode === "only")
    throw new Error("No cached copy of this request on the device.");

  const data = await dedupe(options, key);

  await store(key, data, decision).catch(() => {});

  return { data, fromCache: false, cachedAt: Date.now(), stale: false };
}

/**
 * Collapse identical concurrent reads into one request.
 *
 * A sibling of `services/frappe/dedupe.ts`, never a replacement: that one is
 * explicitly not a response cache, and this does not make it one — the entry is
 * released the moment the request settles.
 */
function dedupe<T>(options: CachedReadOptions<T>, key: string | null): Promise<T> {
  if (!key)
    return options.fetcher();

  const existing = inFlight.get(key) as Promise<T> | undefined;

  if (existing)
    return existing;

  const request = options.fetcher().finally(() => {
    inFlight.delete(key);
  });

  inFlight.set(key, request);

  return request;
}

/**
 * Refresh an entry in the background.
 *
 * ⚠️ Deliberately NOT awaited by the caller, NOT driving any loading flag, and NOT
 * cancellable by one caller's AbortSignal — a component unmounting must not kill a
 * refresh other screens are relying on.
 */
async function revalidate<T>(key: string, options: CachedReadOptions<T>, decision: CacheDecision): Promise<void> {
  if (revalidating.has(key))
    return;

  revalidating.add(key);

  try {
    const fresh = await options.fetcher();

    await store(key, fresh, decision);

    options.onRevalidated?.(harden(fresh));
  }
  catch {
    // ⚠️ A FAILED REFRESH LEAVES THE STORED COPY ALONE. A 500, a dropped
    // connection or a refusal is not evidence that what we hold is wrong, and
    // dropping it would turn a blip into an empty screen.
  }
  finally {
    revalidating.delete(key);
  }
}

/**
 * Cross-tab invalidation.
 *
 * A cashier with the till in one tab and a report in another is one person: a save
 * in either must not leave the other showing what it just replaced. `BroadcastChannel`
 * is already how `syncEngine` talks between tabs, so this follows the same path
 * rather than inventing a second one.
 */
const INVALIDATE_CHANNEL = "alkokh-cache-invalidate";

let channel: BroadcastChannel | null = null;
let channelBound = false;

function ensureChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined")
    return null;

  try {
    channel ??= new BroadcastChannel(INVALIDATE_CHANNEL);

    if (!channelBound) {
      channelBound = true;

      channel.onmessage = (event) => {
        const tags = event?.data?.tags;

        // ⚠️ Delete locally WITHOUT re-broadcasting, or two tabs bounce the same
        // message between them forever.
        if (Array.isArray(tags) && tags.length)
          void deleteByTags(tags).catch(() => {});
      };
    }

    return channel;
  }
  catch {
    return null;
  }
}

/** Everything a mutation made stale, in this tab and every other one. */
export async function invalidateTags(tags: string[]): Promise<number> {
  if (!tags?.length)
    return 0;

  try {
    ensureChannel()?.postMessage({ tags });
  }
  catch {
    // A missing channel only costs the other tabs a slightly stale view.
  }

  try {
    return await deleteByTags(tags);
  }
  catch {
    return 0;
  }
}

/** The sign-out purge: one user's rows, not the whole store. */
export async function invalidateUser(user = currentUserKey()): Promise<void> {
  if (!user)
    return;

  try {
    await deleteByUser(user);
  }
  catch {
    // Nothing to do; the cache is disposable by design.
  }
}

export async function clearCache(): Promise<void> {
  try {
    await clearAll();
  }
  catch {
    // As above.
  }
}

/** Look without fetching — for a freshness badge, or a test. */
export async function peekCache<T>(url: string, params?: unknown, method = "GET"): Promise<CacheEntry<T> | null> {
  const decision = decideCache(url, method);

  if (!decision.cacheable)
    return null;

  return readEntry<T>(buildCacheKey({ url, params, method })).catch(() => null);
}

/** Put a known-good value in without a request (a warm-up, or a test). */
export async function primeCache<T>(url: string, params: unknown, body: T, method = "GET"): Promise<void> {
  const decision = decideCache(url, method);

  if (!decision.cacheable)
    return;

  await store(buildCacheKey({ url, params, method }), body, decision).catch(() => {});
}

/** Test seam: drop in-memory state without touching the database. */
export function resetCacheRuntime(): void {
  inFlight.clear();
  revalidating.clear();
}
